import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2, Paperclip, X, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { EmailTemplateDialog } from "./EmailTemplateDialog";
import { OutlookInbox, MailboxManager } from "./OutlookInbox";
import { QuickReplies } from "./OutlookQuickReplies";
import { MailboxUnlock } from "./MailboxUnlock";
import { hasMailboxToken } from "../lib/mailboxLock";

const STEPS = [
  "ادخل entra.microsoft.com بحساب المدير ← App registrations ← New registration، اكتب اسم (مثلاً WorkZone Mail) واختر «Accounts in this organizational directory only» ثم Register.",
  "من صفحة التطبيق انسخ Application (client) ID و Directory (tenant) ID.",
  "API permissions ← Add a permission ← Microsoft Graph ← Application permissions ← ابحث Mail.Send وفعّلها ← ثم اضغط «Grant admin consent».",
  "Certificates & secrets ← New client secret ← انسخ «Value» فوراً (لا يظهر مرة أخرى) — هذا هو Client Secret.",
  "الصق البيانات هنا مع البريد المرسل (مثل info@yourcompany.sa) ثم احفظ واضغط «إرسال اختبار».",
];
const err = (e, d) => toast.error(e?.response?.data?.detail || d);

const Setup = ({ cfg, onSaved }) => {
  const [f, setF] = useState({ tenant_id: cfg.tenant_id, client_id: cfg.client_id, client_secret: "", sender: cfg.sender, sender_name: cfg.sender_name });
  const [busy, setBusy] = useState("");
  const [testTo, setTestTo] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => { setBusy("save"); try { onSaved(await api.saveOutlook(f)); setF((p) => ({ ...p, client_secret: "" })); toast.success("تم حفظ بيانات الربط"); } catch (e) { err(e, "تعذّر الحفظ"); } finally { setBusy(""); } };
  const test = async () => { setBusy("test"); try { await api.testOutlook(testTo); toast.success("تم إرسال رسالة الاختبار ✅"); onSaved(await api.outlookSettings()); } catch (e) { err(e, "فشل الاختبار"); } finally { setBusy(""); } };
  const fld = (l, k, tid, type = "text") => (
    <label className="space-y-1 text-xs font-semibold text-slate-600">{l}
      <Input data-testid={tid} type="text" dir="ltr" autoComplete={type === "password" ? "new-password" : "off"} name={`wz-outlook-${k}`} data-lpignore="true" data-1p-ignore
        style={type === "password" ? { WebkitTextSecurity: "disc" } : undefined} value={f[k] || ""} onChange={set(k)} className="text-sm font-normal" placeholder={k === "client_secret" && cfg.has_secret ? "•••••• (محفوظ — اتركه فارغاً للإبقاء)" : ""} />
    </label>
  );
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="outlook-setup">
      <details className="rounded-xl bg-blue-50 p-3 text-sm text-blue-900" open={!cfg.configured}>
        <summary className="cursor-pointer font-bold">خطوات إنشاء التطبيق في Microsoft (مرة واحدة)</summary>
        <ol className="mt-2 list-decimal space-y-1.5 pr-5">{STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
      </details>
      <div className="grid gap-3 sm:grid-cols-2">
        {fld("Tenant ID", "tenant_id", "outlook-tenant")}
        {fld("Client ID", "client_id", "outlook-client")}
        {fld("Client Secret", "client_secret", "outlook-secret", "password")}
        {fld("البريد المرسل", "sender", "outlook-sender")}
        <label className="space-y-1 text-xs font-semibold text-slate-600 sm:col-span-2">اسم المرسل الظاهر (اختياري)
          <Input data-testid="outlook-sender-name" value={f.sender_name || ""} onChange={set("sender_name")} placeholder="اسم شركتك" autoComplete="off" name="wz-outlook-sender-name" className="text-sm font-normal" /></label>
      </div>
      <Button data-testid="outlook-save" onClick={save} disabled={!!busy} className="w-full">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ بيانات الربط"}</Button>
      {cfg.configured && (
        <div className="flex gap-2">
          <Input data-testid="outlook-test-to" dir="ltr" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="بريد لاستلام رسالة الاختبار" className="text-sm" />
          <Button data-testid="outlook-test" variant="outline" disabled={!!busy || !testTo.trim()} onClick={test} className="shrink-0">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : "إرسال اختبار"}</Button>
        </div>
      )}
    </div>
  );
};

export const OutlookSettingsCard = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.outlookSettings().then(setCfg).catch(() => {}); }, []);
  if (!cfg) return null;
  if (cfg.managed) return (
    <div className="space-y-1 border-t border-slate-100 pt-2 sm:col-span-2" data-testid="outlook-settings-managed">
      <p className="flex items-center gap-1.5 text-sm font-semibold"><Mail className="h-4 w-4 text-blue-600" /> بريد Outlook</p>
      <p className="text-xs text-slate-500">{cfg.configured ? `مربوط من النظام: ${cfg.sender}` : "سيتم ربط بريد شركتك من قِبل الدعم الفني"}</p>
    </div>);
  if (!cfg.can_configure) return null;
  return (
    <div className="space-y-2 border-t border-slate-100 pt-2 sm:col-span-2" data-testid="outlook-settings-card">
      <p className="flex items-center gap-1.5 text-sm font-semibold"><Mail className="h-4 w-4 text-blue-600" /> ربط بريد Outlook {cfg.configured && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">مربوط: {cfg.sender}</span>}</p>
      <Setup cfg={cfg} onSaved={setCfg} />
    </div>);
};

const Compose = ({ onSent, mailbox }) => {
  const [f, setF] = useState({ to: "", cc: "", subject: "", body: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const send = async () => {
    setBusy(true);
    try {
      const esc = f.body.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
      await api.sendOutlook({ mailbox, to: f.to, cc: f.cc, subject: f.subject, html: `<div dir="rtl" style="font-family:Tahoma,Arial;font-size:14px">${esc}</div>` }, files);
      toast.success("تم إرسال البريد ✅"); setF({ to: "", cc: "", subject: "", body: "" }); setFiles([]); onSent();
    } catch (e) { err(e, "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="outlook-compose">
      <Input data-testid="outlook-to" dir="ltr" value={f.to} onChange={set("to")} placeholder="إلى (افصل بين العناوين بفاصلة)" />
      <Input data-testid="outlook-cc" dir="ltr" value={f.cc} onChange={set("cc")} placeholder="نسخة CC (اختياري)" />
      <Input data-testid="outlook-subject" value={f.subject} onChange={set("subject")} placeholder="الموضوع" />
      <QuickReplies senderName="" onPick={(txt) => setF((p) => ({ ...p, body: p.body.trim() ? `${p.body}\n${txt}` : txt }))} />
      <Textarea data-testid="outlook-body" value={f.body} onChange={set("body")} rows={8} placeholder="نص الرسالة…" />
      <input ref={ref} type="file" multiple hidden data-testid="outlook-files" onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files || [])].slice(0, 10)); e.target.value = ""; }} />
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" data-testid="outlook-attach" onClick={() => ref.current?.click()} className="gap-1.5"><Paperclip className="h-4 w-4" /> إرفاق ملف</Button>
        {files.map((x, i) => <span key={`${x.name}-${i}`} className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs">{x.name}<button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button></span>)}
        <span className="text-[11px] text-slate-400">حد أقصى 3 ميجا لكل ملف</span>
      </div>
      <Button data-testid="outlook-send" onClick={send} disabled={busy || !f.to.trim() || !f.subject.trim()} className="w-full gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال</Button>
    </div>
  );
};

export default function OutlookPanel() {
  const [cfg, setCfg] = useState(null);
  const [sent, setSent] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [boxes, setBoxes] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [mailbox, setMailbox] = useState("");
  const [tab, setTab] = useState("inbox");
  const [, setTick] = useState(0);
  useEffect(() => { api.outlookMailboxes().then((r) => { setBoxes(r.items); setCanManage(r.can_manage); setMailbox((m) => m || (r.items.find((b) => /^it@/i.test(b.address)) || r.items.find((b) => b.system) || r.items.find((b) => !b.locked) || r.items[0])?.address || ""); }).catch(() => {}); }, []);
  const loadSent = useCallback(() => api.outlookSent().then((r) => setSent(r.items)).catch(() => {}), []);
  useEffect(() => { api.outlookSettings().then(setCfg).catch((e) => err(e, "تعذّر التحميل")); loadSent(); }, [loadSent]);
  if (!cfg) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="outlook-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Mail className="h-6 w-6 text-blue-600" /> بريد Outlook
          {cfg.configured && <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs text-emerald-700" data-testid="outlook-status"><CheckCircle2 className="h-3.5 w-3.5" /> {cfg.sender}</span>}</h2>
        <div className="flex flex-wrap gap-2">
        {cfg.configured && canManage && <MailboxManager boxes={boxes} onSaved={setBoxes} />}
        <Button size="sm" variant="outline" data-testid="outlook-email-template-button" onClick={() => setTplOpen(true)} className="gap-1.5"><Mail className="h-4 w-4" /> كليشة الإيميل</Button>
        </div>
      </div>
      {cfg.configured && (
        <div className="flex flex-wrap items-center gap-2" data-testid="outlook-toolbar">
          <select data-testid="outlook-mailbox-select" value={mailbox} onChange={(e) => setMailbox(e.target.value)} dir="ltr"
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-56">
            {boxes.map((b) => <option key={b.address} value={b.address}>{b.locked ? "🔒 " : ""}{b.address}{b.name ? ` — ${b.name}` : ""}</option>)}
          </select>
          <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
            {[["inbox", "صندوق البريد"], ["compose", "رسالة جديدة"], ["log", "سجل الإرسال"]].map(([k, l]) => (
              <button key={k} data-testid={`outlook-tab-${k}`} onClick={() => setTab(k)} className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-white text-blue-700 shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
        </div>
      )}
      <EmailTemplateDialog open={tplOpen} onOpenChange={(v) => { setTplOpen(v); if (!v) loadSent(); }} />
      {!cfg.configured && <p data-testid="outlook-not-configured" className="rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-5 text-center text-sm text-amber-800">
        {cfg.managed ? "بريد شركتك قيد الربط من الدعم الفني — سيتاح الإرسال فور تفعيله." : cfg.can_configure ? "اربط بريد شركتك من «الإعدادات» ← ربط بريد Outlook." : "لم يربط مدير الشركة بريد Outlook بعد."}</p>}
      {cfg.configured && (tab === "inbox" || tab === "compose") && boxes.find((b) => b.address === mailbox)?.locked && !hasMailboxToken(mailbox) ? (
        <MailboxUnlock address={mailbox} onUnlocked={() => { setTick((n) => n + 1); setTimeout(() => setTick((n) => n + 1), 600000); }} />
      ) : <>
      {cfg.configured && tab === "inbox" && mailbox && <OutlookInbox key={mailbox} mailbox={mailbox} />}
      {cfg.configured && tab === "compose" && <Compose onSent={() => { loadSent(); setTab("log"); }} mailbox={mailbox} />}
      </>}
      {tab === "log" && sent.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white" data-testid="outlook-sent">
          <p className="border-b border-slate-100 px-4 py-2 text-sm font-bold">المرسلة</p>
          {sent.map((m) => (
            <div key={m.id} className="border-b border-slate-50 px-4 py-2 text-sm last:border-0">
              <p className="font-semibold">{m.subject}</p>
              <p className="text-xs text-slate-500" dir="ltr">{m.mailbox ? `${m.mailbox} → ` : ""}{m.to.join(", ")}</p>
              <p className="text-[11px] text-slate-400">{new Date(m.sent_at).toLocaleString("ar-SA-u-nu-latn")} · {m.by}{m.attachments?.length ? ` · ${m.attachments.length} مرفق` : ""}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
